import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

import Header from './Header';
import Loading from './Loading';
import HomeRedirect from 'app/components/HomeRedirect';

import {
  logout,
  passwordResetLogin
} from 'shared/actions/userActions';

import {
  updatePassword
} from 'shared/services/user';

import {
  home as homePath,
  login as loginPath
} from 'app/routes';

const PORTAL_ORIGIN = 0;

const PasswordUpdate = () => {
  const [state, setState] = useState({
    loading: true,
    submitting: false,
    error: null,
    emailError: false,
    passwordError: false,
    passwordConfirmationError: false,
    updateComplete: false
  });

  const emailRef = useRef('');
  const passwordRef = useRef('');
  const passwordConfirmationRef = useRef('');

  const user = useSelector(state => state.userReducer.user);
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    token
  } = useParams();

  const setup = async () => {
    // assuming that the user isn't logged in,
    // or if they are then they're fine switching accounts.
    // log them out now in any case
    await dispatch(logout());

    try {
      await dispatch(passwordResetLogin(token));

      setState(state => ({
        ...state,
        loading: false
      }));

    } catch(err) {
      console.error(err);

      let error = 'Sorry, this link is either expired or incorrect. Please request another one.';

      setState(state => ({
        ...state,
        loading: false,
        error
      }));
    }
  }

  useEffect(() => {
    setup();
  }, []);

  const onEmailChange = event => {
    emailRef.current = event.target.value
  }

  const onPasswordChange = event => {
    passwordRef.current = event.target.value;
  }

  const onPasswordConfirmationChange = event => {
    passwordConfirmationRef.current = event.target.value;
  }

  const onSubmit = async event => {
    event.preventDefault();

    let error = null,
      emailError = false,
      passwordError = false,
      passwordConfirmationError = false;

    if (user.fake_email && !emailRef.current) {
      error = 'Please enter an email address.';
      emailError = true;

    } else if (!passwordRef.current) {
      error = 'Please enter a password.';
      passwordError = true;
      passwordConfirmationError = !passwordConfirmationRef.current;

    } else if (!passwordConfirmationRef.current) {
      error = 'Please confirm your password.';
      passwordConfirmationError = true;

    } else if (passwordRef.current !== passwordConfirmationRef.current) {
      error = "Your password and password confirmation don't match.";
      passwordError = true;
      passwordConfirmationError = true;

    } else if (passwordRef.current.length < 6) {
      error = 'Your password must be at least 6 characters.';
      passwordError = true;
      passwordConfirmationError = true;
    }

    if (error) {
      setState(state => ({
        ...state,
        error,
        emailError,
        passwordError,
        passwordConfirmationError
      }));
      return;
    }

    setState(state => ({
      ...state,
      submitting: true
    }));

    const params = {
      email: user.fake_email ? emailRef.current : null,
      new_password: passwordRef.current,
      password_confirmation: passwordConfirmationRef.current
    }

    try {
      await updatePassword(params);

      setState(state => ({
        ...state,
        updateComplete: true
      }));

    } catch(err) {
      console.error(err);

      setState(state => ({
        ...state,
        error: err.error,
        submitting: false
      }));
    }
  }

  if (state.loading) {
    return <Loading />;
  }

  if (state.updateComplete) {
    return <HomeRedirect />;
  }

  // if the token is expired or invalid, then user will be null.
  // just show the error and a back button in this case
  if (!user) {
    return (
      <Form className="login-form">
        <Header />
        <h2>Update Password</h2>
        {state.error && <p className="error">{state.error}</p>}
        <Button className="bs-btn bs-btn-default full-width" type="button" onClick={() => history.push(loginPath)}>
          <span>Back to Login</span>
        </Button>
      </Form>
    )
  }

  let message = null;
  let emailInput = null;

  if (user.fake_email) {
    message = 'Please enter the email and password that you would like to use.';
    emailInput = (
      <Form.Group>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control className={state.emailError ? 'error' : ''}
          type="email"
          name="email"
          placeholder="Email"
          onChange={onEmailChange}
        />
      </Form.Group>
    );
  } else {
    message = 'Enter and confirm your new password.';
  }

  return (
    <Form className="login-form" onSubmit={onSubmit}>
      <Header />
      <h2>Update Password</h2>
      <p>{message}</p>
      {state.error && <p className="error">{state.error}</p>}
      {emailInput}
      <Form.Group>
        <Form.Label htmlFor="password">Password</Form.Label>
        <Form.Control className={state.passwordError ? 'error' : ''}
          type="password"
          name="password"
          placeholder="Password"
          onChange={onPasswordChange}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label htmlFor="password_confirmation">Password confirmation</Form.Label>
        <input className={state.passwordConfirmationError ? 'error' : ''}
          type="password"
          name="password_confirmation"
          placeholder="Password confirmation"
          onChange={onPasswordConfirmationChange}
        />
      </Form.Group>
      <Button variant="primary" className="bs-btn bs-btn-primary full-width" type="submit">
        <span>{state.submitting ? 'Updating password...' : 'Update Password'}</span>
      </Button>
      <Button className="bs-btn bs-btn-default full-width" type="button" onClick={() => history.push(homePath)}>
        <span>Cancel</span>
      </Button>
    </Form>
  )
}

export default PasswordUpdate;
